import { Grid, makeStyles, Theme } from "@material-ui/core";
import Autocomplete from "components/Autocomplete";
import TextField from "components/TextField";
import { theme } from "config/theme";
import useText from "constants/text";
import { AddRequestInput, EReccurenceType, ERequestType, ReccurenceOptionsInput } from "generated/graphql";
import React from "react";
import { formatDate as DateFormat } from "utils";
import { formatDate, isFieldWithValue, ReccurenceType } from "../utils";
import ReccurenceModal from "./ReccurenceModal";

interface RecurenceStyleType {
  padding?: string;
}

const useStyles = makeStyles<Theme, RecurenceStyleType>({
  root: {
    padding: 25,
  },
  reccurence: {
    padding: ({ padding }) => padding || "17px !important",
  },
  endDate: {
    padding: " 0 40px !important",
    [theme.breakpoints.down("xs")]: {
      marginTop: -2,
      marginBottom: 54,
    },
  },
});

export interface IReccurence {
  reccurence: AddRequestInput;
  onReccurenceChange: (reccurenceType: EReccurenceType, options?: ReccurenceOptionsInput) => void;
  onchangeEndRequestDate: (event: React.ChangeEvent<HTMLInputElement>) => void;
  startRequestDate?: string;
  endRequestDate: string;
  error?: boolean;
  dateError?: boolean;
  forSamaPapser?: boolean;
  noEndDate?: boolean;
  padding?: string;
}

export const customReccurenceOptions = [
  EReccurenceType.Days,
  EReccurenceType.Months,
  EReccurenceType.Weeks,
  EReccurenceType.Years,
];

export default function Reccurence({
  reccurence,
  onReccurenceChange,
  endRequestDate,
  onchangeEndRequestDate,
  startRequestDate = "",
  error = false,
  dateError = false,
  forSamaPapser = false,
  noEndDate,
  padding,
}: IReccurence) {
  const { text } = useText();
  const [activeEndDate, setActiveEndDate] = React.useState<boolean>(false);
  const [launchModal, setLaunchModal] = React.useState<boolean>(false);
  const [value, setValue] = React.useState(null);
  const classes = useStyles({ padding });

  const reccurenceOptionsSelect = [
    {
      value: EReccurenceType.DoesNotRepeat,
      description: text.requestQuote.reccurence_not_repeat,
      enableCustomisation: false,
    },
    {
      value: EReccurenceType.Everyday,
      description: text.requestQuote.reccurence_daily,
      enableCustomisation: false,
    },
    {
      value: ReccurenceType.Custom,
      matches: customReccurenceOptions,
      description: text.requestQuote.custom,
      enableCustomisation: true,
    },
  ];

  const recurrenceOptionCustomized =
    reccurence?.type !== ERequestType.Mad ? reccurenceOptionsSelect.slice(0) : reccurenceOptionsSelect;

  React.useEffect(() => {
    if (reccurence?.reccurenceType && reccurence?.reccurenceType !== EReccurenceType.DoesNotRepeat) {
      setActiveEndDate(true);
    } else setActiveEndDate(false);
  }, [reccurence]);

  const onChange = (reccurence: any) => {
    setValue(reccurence);
    if (reccurence?.enableCustomisation) {
      setLaunchModal(true);
    } else {
      onReccurenceChange(reccurence?.value);
    }
  };

  const onCancelReccurence = () => {
    setLaunchModal(false);
  };

  const onValidateCustomization = (reccurenceType: EReccurenceType, options: ReccurenceOptionsInput) => {
    onReccurenceChange(reccurenceType, options);
    setLaunchModal(false);
  };

  const asDescription = (option: any) =>
    recurrenceOptionCustomized.find((e) => e.value === option.value || e?.matches?.includes(option.value))
      ?.description || "";

  const renderReccurenceOption = () => {
    const type: EReccurenceType | ReccurenceType = reccurence?.reccurenceType;
    if (customReccurenceOptions.includes(reccurence?.reccurenceType)) {
      return {
        value: ReccurenceType.Custom,
        matches: customReccurenceOptions,
        description: text.requestQuote.custom,
        enableCustomisation: true,
      };
    }
    return recurrenceOptionCustomized.find((option) => option.value === type);
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} className={classes.reccurence}>
        <Autocomplete
          label={text.requestQuote.reccurence_repeat_label}
          options={recurrenceOptionCustomized}
          getOptionLabel={(option: any) => asDescription(option)}
          getOptionSelected={(option: any, selection: any) => option?.values?.includes(selection?.value)}
          onChange={(event: any, value: any) => onChange(value)}
          value={renderReccurenceOption() || value}
          error={error}
          helperText={error ? text.all.this_field_is_required : ""}
        />
      </Grid>
      {!noEndDate && activeEndDate && (
        <Grid item xs={12} className={classes.endDate}>
          <TextField
            type="date"
            fullWidth
            label={text.all.end_date}
            name="endDate"
            value={DateFormat(endRequestDate, "YYYY-MM-dd") || formatDate(0)}
            onChange={onchangeEndRequestDate}
            inputProps={{ min: formatDate(0) }}
            error={dateError}
            helperText={
              !isFieldWithValue(endRequestDate)
                ? text.all.this_field_is_required
                : dateError
                ? text.all.intervall_date_error
                : ""
            }
          />
        </Grid>
      )}
      {launchModal && (
        <ReccurenceModal
          open={launchModal}
          startRequestDate={startRequestDate}
          onCancel={onCancelReccurence}
          onValidateCustomization={onValidateCustomization}
          reccurence={reccurence}
        />
      )}
    </Grid>
  );
}
